import React, { useEffect, useMemo, useState, memo } from "react";
import {
  Row,
  Col,
  Card as BootstrapCard,
  Spinner,
  Alert,
} from "react-bootstrap";

import Card from "../../../components/Card";
import DashboardService from "../../../services/pbumku/DashboardService";
import CountUp from "react-countup";
import { usePeriode } from "../context/PeriodeContext";

const ReportSmkhp = memo(() => {
  const DashService = useMemo(() => new DashboardService(), []);
  const { periode } = usePeriode();
  const { startDate, endDate } = periode;
  const yearPeriod = startDate ? startDate.split("-")[0] : new Date().getFullYear();

  const [jumfreq, setJumfreq] = useState(0);
  const [jumVol, setJumVol] = useState(0);
  const [nilaiIDR, setNilaiIDR] = useState(0);
  const [nilaiUSD, setNilaiUSD] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const kdUPT = "00.1";

  const apiStartDate = startDate ? `${startDate} 00:00:00` : "";
  const apiEndDate = endDate ? `${endDate} 23:59:59` : "";

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) return;

      try {
        setLoading(true);
        setError(null);

        const [resFreq, resVol, resNilai] = await Promise.all([
          DashService.getJumlahFrequensi(
            kdUPT,
            apiStartDate,
            apiEndDate
          ),
          DashService.getJumlahVolume(
            kdUPT,
            apiStartDate,
            apiEndDate
          ),
          DashService.getJumlahNilai(
            kdUPT,
            apiStartDate,
            apiEndDate
          ),
        ]);

        const totalFrekuensi = Number(resFreq?.data ?? 0);
        const totalVolume = Number(resVol?.data?.totalVolume ?? 0);
        const totalNilaiIDR = Number(resNilai?.data?.totalNilaiIDR ?? 0);
        const totalNilaiUSD = Number(resNilai?.data?.totalNilaiUSD ?? 0);

        setJumfreq(totalFrekuensi);
        setJumVol(totalVolume);
        setNilaiIDR(totalNilaiIDR);
        setNilaiUSD(totalNilaiUSD);
      } catch (err) {
        console.error("Fetch rekap SMKHP error:", err);

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Gagal mengambil data rekap SMKHP."
        );

        setJumfreq(0);
        setJumVol(0);
        setNilaiIDR(0);
        setNilaiUSD(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    DashService,
    kdUPT,
    startDate,
    endDate,
    apiStartDate,
    apiEndDate,
  ]);

  const kpiData = [
    {
      title: "Frekuensi",
      value: jumfreq,
      color: "primary",
      decimals: 0,
      suffix: "",
    },
    {
      title: "Volume (Ton)",
      value: jumVol / 1000,
      color: "success",
      decimals: 2,
      suffix: "",
    },
    {
      title: "Nilai IDR (Juta)",
      value: nilaiIDR / 1000000,
      color: "info",
      decimals: 2,
      suffix: " M",
    },
    {
      title: "Nilai USD",
      value: nilaiUSD,
      color: "warning",
      decimals: 2,
      suffix: "",
    },
  ];

  return (
    <BootstrapCard className="shadow-sm rounded-3 mb-3 border-0 h-100">
      <BootstrapCard.Body>
        <div className="mb-4">
          <h6 className="fw-bold text-uppercase mb-1">
            Rekap Sertifikasi Mutu SMKHP
          </h6>

          <small className="text-muted">
            Periode Tahun {yearPeriod}
          </small>
        </div>

        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "150px" }}
          >
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted">Memuat data...</span>
            </div>
          </div>
        )}

        {!loading && error && (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Row className="g-3">
            {kpiData.map((item, index) => (
              <Col lg={3} md={6} sm={12} key={index}>
                <Card className="border-0 shadow-sm overflow-hidden h-100">
                  <Card.Body
                    className={`
                      border-start
                      border-4
                      border-${item.color}
                      h-100
                    `}
                  >
                    <div
                      className="d-flex flex-column justify-content-center"
                      style={{ minHeight: "75px" }}
                    >
                      <p className="text-muted small mb-1 fw-bold">
                        {item.title}
                      </p>

                      <h3 className="mb-0 fw-bold">
                        <CountUp
                          end={Number(item.value) || 0}
                          decimals={item.decimals}
                          duration={1.5}
                          separator="."
                          decimal=","
                        />

                        {item.suffix && (
                          <span className="small text-muted ms-1">
                            {item.suffix}
                          </span>
                        )}
                      </h3>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </BootstrapCard.Body>
    </BootstrapCard>
  );
});

ReportSmkhp.displayName = "ReportSmkhp";

export default ReportSmkhp;
